import { useEffect, useState } from "react"
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material"
import { chatApi, projectApi, taskApi } from "../../api"

/**
 * Attach a project and a task to a conversation.
 *
 * ── The constraint that shapes this ─────────────────────────────────────────
 *
 * A thread belongs to exactly one organization (`client_id`), and the server
 * refuses a project or task from any other. So both pickers are loaded scoped to
 * the thread's client rather than showing everything the user can see — offering
 * a choice the server will reject is worse than not offering it.
 *
 * The task list narrows further once a project is chosen, because the server also
 * refuses a task that is not in the attached project: a header showing a project
 * and an unrelated task reads as a bug in the link rather than a bad attachment.
 */
export default function AttachWorkDialog({ open, room, onClose, onAttached }) {
  const [projects, setProjects] = useState([])
  const [tasks, setTasks] = useState([])
  const [project, setProject] = useState(null)
  const [task, setTask] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !room) return
    setError(null)
    setProject(null)
    setTask(null)

    projectApi
      .list({ client_id: room.client_id, limit: 200 })
      .then((data) => {
        const list = data.projects || []
        setProjects(list)
        setProject(list.find((p) => Number(p.id) === Number(room.project_id)) || null)
      })
      .catch(() => setProjects([]))
  }, [open, room])

  useEffect(() => {
    if (!open || !room) return
    const params = { client_id: room.client_id, limit: 200 }
    if (project?.id) params.project_id = project.id

    taskApi
      .list(params)
      .then((data) => {
        const list = data.tasks || []
        setTasks(list)
        setTask((current) => {
          const keep = list.find((t) => Number(t.id) === Number(current?.id ?? room.task_id))
          return keep || null
        })
      })
      .catch(() => setTasks([]))
  }, [open, room, project])

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await chatApi.updateRoom(room.id, {
        project_id: project?.id ?? null,
        task_id: task?.id ?? null,
      })
      onAttached(result.room)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach work to this conversation</DialogTitle>
      <DialogContent dividers>
        <DialogContentText variant="body2" sx={{ mb: 2.5 }}>
          Linking a project or a task makes this thread visible from the work itself, so the conversation and what it is
          about stay together.
        </DialogContentText>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Stack spacing={2.5}>
          <Autocomplete
            options={projects}
            value={project}
            onChange={(_, value) => {
              setProject(value)
              // A task from the old project would no longer be valid.
              setTask(null)
            }}
            getOptionLabel={(option) => `${option.code} · ${option.name}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Project" placeholder="None" />}
          />

          <Autocomplete
            options={tasks}
            value={task}
            onChange={(_, value) => setTask(value)}
            getOptionLabel={(option) => `${option.task_code} · ${option.title}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Task or issue"
                placeholder="None"
                helperText={project ? `Showing tasks in ${project.code}` : "Showing every task for this client"}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? "Saving…" : "Attach"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
